import { memo } from "react";
import type { StyleProp, ViewStyle } from "react-native";
import type { SvgProps } from "react-native-svg";
import {
  Add,
  Additional,
  AddToCalendar,
  AlertFilled,
  ArrowDown,
  ArrowLtr,
  Assigned,
  BalanceFilled,
  BalanceOutline,
  Bath,
  Bed,
  Bookmark,
  Cabinet,
  Cake,
  Calendar,
  CalendarFilled,
  CalendarHeart,
  CalendarSync,
  Camera,
  Card,
  Certificate,
  Certificate2,
  Chat,
  Chat2,
  ChatFilled,
  Checkbox,
  Checkmark,
  Chevron,
  Clean,
  ClockOutline,
  Close,
  Cog,
  CouponFilled,
  CrossFull,
  CrossOutline,
  DoneFull,
  Driving,
  Edit,
  Envelope,
  Flag,
  Gb,
  HeartFilled,
  Image as ImageIcon,
  Info,
  InfoFilled,
  Insurance,
  Invoice,
  Localization,
  Lock,
  Menu,
  Minus,
  MyHome,
  No,
  OffersFilled,
  OffersOutline,
  OneTime,
  Oven,
  Phone,
  Pin,
  Pl,
  PlayFilled,
  Plus,
  Profile,
  QuestionmarkOutline,
  Refresh,
  Shield,
  ShieldStar,
  Star,
  StarOfLife,
  StarOutline,
  StepappLogo,
  Stepcover,
  StopFilled,
  Subscription,
  Sv,
  ThumbsUp,
  Trophy,
  TrophyOutline,
  Ua,
  Verified,
  VerifiedBig,
  Wallet,
  Warning,
} from "./icons";

type IconComponent = React.ComponentType<SvgProps>;

type HeartIconComponent = React.ComponentType<
  SvgProps & {
    gradientPoint?: { offset: string; stopColor: string }[];
  }
>;

const icons: Record<string, IconComponent | HeartIconComponent> = {
  "add-to-calendar": AddToCalendar,
  "alert-filled": AlertFilled,
  "balance-filled": BalanceFilled,
  "balance-outline": BalanceOutline,
  "clock-outline": ClockOutline,
  "calendar-sync": CalendarSync,
  "calendar-filled": CalendarFilled,
  "calendar-heart": CalendarHeart,
  "chat-filled": ChatFilled,
  "chevron-down": Chevron,
  "chevron-left": Chevron,
  "chevron-right": Chevron,
  "chevron-up": Chevron,
  "cross-outline": CrossOutline,
  "info-filled": InfoFilled,
  "my-home": MyHome,
  "offers-filled": OffersFilled,
  "play-filled": PlayFilled,
  "questionmark-outline": QuestionmarkOutline,
  "stop-filled": StopFilled,
  "shield-star": ShieldStar,
  "offers-outline": OffersOutline,
  "one-time": OneTime,
  "star-outline": StarOutline,
  "thumbs-up": ThumbsUp,
  "trophy-outline": TrophyOutline,
  "verified-big": VerifiedBig,
  add: Add,
  additional: Additional,
  arrowLtr: ArrowLtr,
  arrowDown: ArrowDown,
  assigned: Assigned,
  bath: Bath,
  bed: Bed,
  bookmark: Bookmark,
  cabinet: Cabinet,
  cake: Cake,
  calendar: Calendar,
  camera: Camera,
  card: Card,
  certificate: Certificate,
  certificate2: Certificate2,
  chat: Chat,
  chat2: Chat2,
  checkbox: Checkbox,
  checkmark: Checkmark,
  clean: Clean,
  close: Close,
  cog: Cog,
  coupon: CouponFilled,
  cross: CrossFull,
  done: DoneFull,
  driving: Driving,
  edit: Edit,
  envelope: Envelope,
  flag: Flag,
  en: Gb,
  heart: HeartFilled,
  image: ImageIcon,
  info: Info,
  insurance: Insurance,
  invoice: Invoice,
  lock: Lock,
  localization: Localization,
  menu: Menu,
  minus: Minus,
  no: No,
  oven: Oven,
  pin: Pin,
  phone: Phone,
  pl: Pl,
  plus: Plus,
  profile: Profile,
  refresh: Refresh,
  shield: Shield,
  starOfLife: StarOfLife,
  sv: Sv,
  star: Star,
  stepappLogo: StepappLogo,
  stepcover: Stepcover,
  subscription: Subscription,
  trophy: Trophy,
  uk: Ua,
  verified: Verified,
  wallet: Wallet,
  warning: Warning,
};

type Props = {
  color?: string;
  name: keyof typeof icons;
  size?: number;
  style?: StyleProp<ViewStyle>;
  gradientPoint?: { offset: string; stopColor: string }[];
};

const Icon = ({ name, color = "#000", size = 24, style = {}, gradientPoint }: Props) => {
  const IconComponent = icons[name];
  // @ts-expect-error
  const { transform = [], ...restStyle } = style;

  const scaleStyle = {
    transform: [
      { scaleX: size / 24 },
      { scaleY: size / 24 },
      name === "chevron-right" && { rotate: "180deg" },
      name === "chevron-up" && { rotate: "90deg" },
      name === "chevron-down" && { rotate: "-90deg" },
      ...transform,
    ].filter(Boolean) as ToDo,
  };

  if (!IconComponent) {
    return null;
  }

  return (
    <IconComponent color={color} style={[scaleStyle, restStyle]} gradientPoint={gradientPoint} />
  );
};

export type IconNames = keyof typeof icons;

export default memo(Icon);
